import { useState, useEffect, useCallback } from 'react';
import type { Settings } from '../types/interfaces';

const defaultSettings: Settings = {
  accentColor: '#6366f1',
  gradientBg: true,
  fontSize: 16,
  contrastLevel: 'normal',
  reduceMotion: false,
  largerText: false,
  itemsPerPage: 25,
  autoRefresh: 60,
  compactMode: false,
  showIcons: true,
  showVolume: true,
  numberFormat: 'compact',
  currency: 'usd',
  currencyUnlocked: false,
  portfolioAnalyticsUnlocked: false,
  multiplePortfoliosUnlocked: false,
  currentPortfolioId: null
};

export function useSettings() {
  const [settings, setSettings] = useState<Settings>(() => {
    const saved = localStorage.getItem('cryptoscopeSettings');
    if (saved) {
      try {
        return { ...defaultSettings, ...JSON.parse(saved) };
      } catch {
        return defaultSettings;
      }
    }
    return defaultSettings;
  });

  // Apply settings immediately on mount
  useEffect(() => {
    applySettings(settings);
  }, []); // Only run once on mount

  useEffect(() => {
    localStorage.setItem('cryptoscopeSettings', JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

  const updateSettings = useCallback((updates: Partial<Settings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  }, []);

  const applySettings = useCallback((s: Settings) => {
    // Ensure dark mode is always active
    document.documentElement.classList.add('dark');
    document.documentElement.classList.remove('light');
    
    // Apply accessibility settings
    document.documentElement.style.setProperty('--font-size', `${s.fontSize}px`);
    if (s.contrastLevel === 'high') {
      document.documentElement.classList.add('high-contrast');
    } else {
      document.documentElement.classList.remove('high-contrast');
    }
    if (s.reduceMotion) {
      document.documentElement.classList.add('reduce-motion');
    } else {
      document.documentElement.classList.remove('reduce-motion');
    }
  }, []);

  return { settings, updateSettings };
}

